
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { getMatrixClient, getMatrixServer, getRoomInitialSync } from '../matrix/matrixFuntions';
import { useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import { Send, Paperclip, X } from 'lucide-react';
import { Button } from 'react-bootstrap';
import { authenticateMatrixClient } from '../../services/matrixAuthService.js';

const SendTextSchema = Yup.object().shape({
    apptMessage: Yup.string().min(1, "Message cannot be empty").required("Please type a message"),
});
export default function SendMessage({ loggedInUser, synapseRoomId, dispatch }) {
    const chatDetails = useSelector(state => state.chatDetails);
    let matrixClient = chatDetails.matrixClient;
    
    console.log('=== SEND MESSAGE COMPONENT DEBUG ===');
    console.log('Matrix client available:', !!matrixClient);
    console.log('Matrix client access token:', matrixClient?.getAccessToken());
    console.log('Matrix client user ID:', matrixClient?.getUserId());
    console.log('Synapse Room ID:', synapseRoomId);
    console.log('=== END SEND MESSAGE DEBUG ===');

    const [fileToSend, setFileToSend] = useState(null);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const sendMessageWithClient = (client, messageText, roomId) => {
        const content = {
            body: messageText,
            msgtype: "m.text",
        };
        var sendEventResponse = client.sendEvent(roomId, "m.room.message", content, "");
        sendEventResponse.then((response) => {
            console.log("MESSAGE SENT: ", response);
            // Don't immediately sync - rely on real-time event listener for better UX
            // The message will appear through the Matrix event listener
            console.log("Message sent successfully - waiting for real-time event");
        }).catch((error) => {
            console.log("MESSAGE NOT SENT-->ERROR:", error);
            // Only sync on error to refresh the state
            getRoomInitialSync(roomId, client, dispatch);
        });
    };

    const sendTextMessage = (messageText) => {

        if (!matrixClient || !matrixClient.getAccessToken()) {
            console.log('=== MATRIX AUTHENTICATION TRIGGER ===');
            console.log('Matrix client available:', !!matrixClient);
            console.log('Matrix client has token:', !!(matrixClient && matrixClient.getAccessToken()));
            console.log('User has synapse_token:', !!(loggedInUser && loggedInUser.synapse_token));
            
            // Try to authenticate Matrix client first
            if (loggedInUser && (loggedInUser.synapse_token || loggedInUser.password)) {
                console.log('Triggering Matrix authentication...');
                authenticateMatrixClient(loggedInUser, dispatch)
                    .then(() => {
                        console.log('Matrix authentication completed, retrying message send...');
                        // Retry sending the message after authentication
                        setTimeout(() => sendTextMessage(messageText), 1000);
                    })
                    .catch((error) => {
                        console.error('Matrix authentication failed:', error);
                        // Fall back to creating unauthenticated client
                        console.log('Falling back to unauthenticated client...');
                        matrixClient = getMatrixClient(loggedInUser.synapse_token, loggedInUser.synapse_user_id);
                        sendMessageWithClient(matrixClient, messageText, synapseRoomId);
                    });
                return; // Exit early, will retry after authentication
            } else {
                console.log('No Matrix credentials available, creating unauthenticated client...');
                matrixClient = getMatrixClient(loggedInUser.synapse_token, loggedInUser.synapse_user_id);
            }
        }
        
        // If we have a properly authenticated client, send the message
        if (matrixClient && matrixClient.getAccessToken()) {
            console.log('Sending message with authenticated client...');
            sendMessageWithClient(matrixClient, messageText, synapseRoomId);
        } else {
            console.log('No authenticated client available, cannot send message');
        }

    }

    const sendImage = async (filename, imgUrl, file) => {
        if (!matrixClient) {
            matrixClient = getMatrixClient(loggedInUser.synapse_token, loggedInUser.synapse_user_id);
        }

        const content = {
            body: filename,
            info: {
                mimetype: file.type,
                size: file.size,
                // Optional: Add dimensions if available
                h: file.height || undefined,
                w: file.width || undefined,
            },
            msgtype: "m.image",
            url: imgUrl
        };

        try {
            const response = await matrixClient.sendMessage(synapseRoomId, content);
            console.log("IMAGE MESSAGE SENT: ", response);
            console.log("📤 Image message sent successfully - waiting for real-time event");
        } catch (error) {
            console.log("IMAGE MESSAGE NOT SENT-->ERROR:", error);
            getRoomInitialSync(synapseRoomId, matrixClient, dispatch);
        }
    }

    const hiddenFileInput = useRef(null); // ADDED


    const handleChange = event => {
        const fileUploaded = event.target.files[0];
        console.log("File to be uploaded:", fileUploaded)
        handleFile(fileUploaded);                   // ADDED
    };

    const uploadFile = async (file) => {
        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            dispatch({
                type: 'setErrorMsg',
                payload: 'File size must be less than 10MB'
            });
            setTimeout(() => {
                dispatch({ type: 'setErrorMsg', payload: null });
            }, 3000);
            return "";
        }

        var infoMatrix = {
            "name": file.name,
            "type": file.type,
            "rawResponse": 0,
            "onlyContentUri": false
        };

        // Debug: Check if loggedInUser has required properties
        console.log("DEBUG: loggedInUser:", loggedInUser);
        console.log("DEBUG: access_token:", loggedInUser?.access_token);
        console.log("DEBUG: synapse_user_id:", loggedInUser?.synapse_user_id);

        if (!loggedInUser?.access_token || !loggedInUser?.synapse_user_id) {
            console.error("ERROR: Missing access_token or synapse_user_id in loggedInUser");
            dispatch({
                type: 'setErrorMsg',
                payload: "Authentication error: Missing user credentials for file upload"
            });
            return "";
        }

        var matrixServer = getMatrixServer(loggedInUser.access_token, loggedInUser.synapse_user_id)
        console.log("DEBUG: matrixServer created:", matrixServer);
        console.log("DEBUG: matrixServer homeserver URL:", matrixServer.getHomeserverUrl());

        var uploadFileResp = await matrixServer.uploadContent(file, infoMatrix)
            .then((response) => {
                console.log("✅ Upload successful:", response);
                return response;
            })
            .catch((error) => {
                console.error("ERROR: Matrix uploadContent failed:", error);
                console.error("ERROR details:", error.message, error.httpStatus);
                console.error("ERROR stack:", error.stack);

                // Handle specific authentication errors
                if (error.httpStatus === 401) {
                    dispatch({
                        type: 'setErrorMsg',
                        payload: "Authentication failed: Please check your Matrix credentials"
                    });
                } else if (error.httpStatus === 405) {
                    dispatch({
                        type: 'setErrorMsg',
                        payload: "File upload not available: Server configuration issue. Please contact administrator."
                    });
                } else if (error.httpStatus === 413) {
                    dispatch({
                        type: 'setErrorMsg',
                        payload: "File too large: Maximum size is 10MB"
                    });
                } else {
                    dispatch({
                        type: 'setErrorMsg',
                        payload: "File upload failed: " + error.message
                    });
                }
                return null;
            });
        if (uploadFileResp) {
            console.log("UPLOAD RESPONSE: ", uploadFileResp)
            console.log("CONTENT URI: ", uploadFileResp.content_uri)

            // Determine message type based on file type
            if (file.type.startsWith('image/')) {
                sendImage(file.name, uploadFileResp.content_uri, file)
            } else {
                sendFile(file.name, uploadFileResp.content_uri, file)
            }
            return uploadFileResp.content_uri;
        }
        return ""
    }

    const handleFile = (file) => {
        console.log("File selected:", file);
        setFileToSend(file);
    };

    const clearInput = (resetForm, setFieldValue) => {
        resetForm();
        setFileToSend(null);
        setFieldValue('apptMessage', '');
    };

    const sendFile = (filename, fileUrl, file) => {
        if (!matrixClient) {
            matrixClient = getMatrixClient(loggedInUser.access_token, loggedInUser.synapse_user_id);
        }

        const content = {
            body: filename,
            filename: filename,
            info: {
                mimetype: file.type,
                size: file.size
            },
            msgtype: "m.file",
            url: fileUrl
        };

        var sendEventResponse = matrixClient.sendEvent(synapseRoomId, "m.room.message", content, "");
        sendEventResponse.then((response) => {
            console.log("FILE MESSAGE SENT: ", response);
            console.log("📤 File message sent successfully - waiting for real-time event");
        }).catch((error) => {
            console.log("FILE MESSAGE NOT SENT-->ERROR:", error);
            getRoomInitialSync(synapseRoomId, matrixClient, dispatch);
        });
    }

    const handleFileSend = async () => {
        if (!fileToSend) return;
        await uploadFile(fileToSend);
        setFileToSend(null);
    };


    return (
        <Formik
            initialValues={{
                apptMessage: '',
            }}
            validationSchema={SendTextSchema}
            onSubmit={(values, { resetForm, setSubmitting }) => {
                console.log("SUBMITTING:", values);
                sendTextMessage(values.apptMessage);
                resetForm();
                setSubmitting(false);
            }}
        >
            {({ errors, touched, handleBlur, isSubmitting, resetForm, setFieldValue, values }) => (
                <Form id="send-message-form" className="w-full">
                    <div className="bg-white border-t border-gray-200 px-4 py-3">
                        {/* File Preview */}
                        {fileToSend && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                            <Paperclip className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{fileToSend.name}</p>
                                            <p className="text-xs text-gray-500">{(fileToSend.size / 1024).toFixed(1)} KB</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            type="button"
                                            onClick={handleFileSend}
                                            size="sm"
                                            disabled={isSubmitting}
                                            className="d-flex align-items-center bg-teal-700 hover:bg-teal-800 text-white border-teal-700"
                                        >
                                            <Send className="w-4 h-4 me-1" />
                                            Send
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={() => setFileToSend(null)}
                                            variant="outline-secondary"
                                            size="sm"
                                            className="d-flex align-items-center"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Message Input Row */}
                        <div className="flex items-center space-x-2">
                            {/* Text Input with integrated buttons */}
                            <div className="flex-1 position-relative">
                                <Field
                                    as="textarea"
                                    id="input_appt_reason"
                                    name="apptMessage"
                                    onFocus={() => setIsInputFocused(true)}
                                    onBlur={(e) => {
                                        handleBlur(e);
                                        setIsInputFocused(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault(); // Prevent new line
                                            // Trigger form submission
                                            e.target.form?.requestSubmit();
                                        }
                                        // Allow Shift+Enter to create new line (default behavior)
                                    }}
                                    placeholder="Type your message..."
                                    rows={1}
                                    className={`w-full px-3 py-2 pr-20 form-control ${errors.apptMessage && touched.apptMessage ? 'is-invalid' : ''}`}
                                    style={{ minHeight: '38px', maxHeight: '120px', resize: 'none' }}
                                />
                                
                                {/* Clear Button */}
                                {(values.apptMessage || isInputFocused) && (
                                    <button
                                        type="button"
                                        onClick={() => clearInput(resetForm, setFieldValue)}
                                        className="position-absolute border-0 bg-transparent p-1 text-gray-400 hover:text-gray-600"
                                        style={{ right: '38px', top: '50%', transform: 'translateY(-50%)' }}
                                        title="Clear message"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}

                                {/* Send Button - Integrated */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !values.apptMessage.trim()}
                                    className="position-absolute border-0 bg-transparent p-1 text-teal-700 hover:text-teal-800 disabled:text-muted"
                                    style={{ right: '8px', top: '50%', transform: 'translateY(-50%) rotate(45deg)' }}
                                    title="Send message"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Attachment Button */}
                            <div className="position-relative">
                                <input
                                    type='file'
                                    ref={hiddenFileInput}
                                    onChange={handleChange}
                                    className='d-none'
                                />
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    disabled={isSubmitting}
                                    onClick={() => hiddenFileInput.current?.click()}
                                    className="d-flex align-items-center"
                                    title="Attach file"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {errors.apptMessage && touched.apptMessage && (
                            <div className="mt-2 text-danger small d-flex align-items-center">
                                <svg className="w-4 h-4 me-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errors.apptMessage}
                            </div>
                        )}
                    </div>
                </Form>
            )}
        </Formik>
    );

}